import * as React from 'react';
import { DeviceType, Site } from '@ixicare/resources';
import { useSelectedSite } from '~/hooks/useSelectedSite';
import { FieldValues } from '../components/DeviceConfigForm/schema';

/**
 * The site's stored configuration for one device type, as the layer a device's own settings sit on
 * top of.
 *
 * `undefined` until it is known, so a form seeded before the fetch lands flags nothing rather than
 * flagging everything. A site the configuration service has never synced, or a type the site has
 * no values for, is an empty object: nothing to compare against, which is not the same as an error.
 *
 * Failures are swallowed on purpose. This drives an annotation, not the form's contents -- a
 * device's configuration must still be editable when the site layer cannot be read.
 */
export const useSiteConfigValues = (
  deviceType: DeviceType | null | undefined,
  siteId?: number | null
): FieldValues | undefined => {
  const [selectedSite] = useSelectedSite();
  const [values, setValues] = React.useState<FieldValues>();

  const site = siteId ?? selectedSite?.uuid;

  React.useEffect(() => {
    if (!site || !deviceType) return;
    let cancelled = false;
    Site.getSiteDeviceSettings(site)
      .then((config) => {
        if (!cancelled) setValues(config[deviceType] ?? {});
      })
      .catch(() => {
        if (!cancelled) setValues(undefined);
      });
    return () => {
      cancelled = true;
    };
  }, [site, deviceType]);

  return values;
};
